import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Car, Pencil, Plus } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useFetch } from "@/lib/hooks";
import { can, da, fmtDate, km } from "@/lib/format";
import { Empty, Info, Loading, PageHeader, Panel, StatusBadge } from "@/components/common";
import { ClientDialog, VehicleDialog } from "@/components/forms";
import { Button } from "@/components/ui/button";

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { data: c, reload } = useFetch<any>(`/clients/${id}`);
  const [edit, setEdit] = useState(false);
  const [veh, setVeh] = useState(false);
  if (!c) return <Loading />;
  const write = can(user?.role, "clients_write");
  return (
    <div>
      <PageHeader eyebrow={c.kind === "entreprise" ? "Client entreprise" : "Client particulier"} title={c.name}
        actions={write && <>
          <Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-client-btn"><Pencil className="mr-2 h-4 w-4" />Modifier</Button>
          <Button onClick={() => setVeh(true)} data-testid="add-vehicle-btn"><Plus className="mr-2 h-4 w-4" />Véhicule</Button>
        </>} />
      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="Coordonnées">
          <div className="grid grid-cols-2 gap-5">
            <Info label="Téléphone" value={c.phone} testId="client-phone" />
            <Info label="Email" value={c.email} />
            <Info label="Adresse" value={c.address} />
            {c.kind === "entreprise" && <><Info label="NIF" value={c.nif} /><Info label="RC" value={c.rc} /></>}
          </div>
        </Panel>
        <Panel title={`Véhicules (${c.vehicles.length})`} className="lg:col-span-2">
          {c.vehicles.length === 0 ? <Empty text="Aucun véhicule" /> : (
            <div className="grid gap-3 sm:grid-cols-2">
              {c.vehicles.map((v: any) => (
                <Link key={v.id} to={`/vehicules/${v.id}`} className="flex items-center gap-3 rounded-sm border border-border p-4 transition-colors hover:border-primary/60" data-testid={`client-vehicle-${v.id}`}>
                  <Car className="h-5 w-5 text-muted-foreground" />
                  <div><div className="font-mono-data font-semibold">{v.plate}</div><div className="text-xs text-muted-foreground">{v.brand} {v.model} · {km(v.mileage)}</div></div>
                </Link>
              ))}
            </div>
          )}
        </Panel>
        <Panel title="Historique des passages" className="lg:col-span-2">
          {c.receptions.length === 0 ? <Empty text="Aucun passage" /> : c.receptions.map((r: any) => (
            <Link key={r.id} to={`/receptions/${r.id}`} className="flex items-center justify-between border-b border-border py-3 last:border-0 hover:bg-accent/40" data-testid={`client-history-${r.id}`}>
              <div><div className="font-mono-data text-sm font-semibold">{r.number}</div><div className="text-xs text-muted-foreground">{fmtDate(r.created_at)} · {r.customer_request}</div></div>
              <StatusBadge status={r.status} />
            </Link>
          ))}
        </Panel>
        {can(user?.role, "billing") && (
          <Panel title="Factures">
            {c.invoices.length === 0 ? <Empty text="Aucune facture" /> : c.invoices.map((i: any) => (
              <Link key={i.id} to={`/factures/${i.id}`} className="flex justify-between border-b border-border py-2.5 text-sm last:border-0">
                <span className="font-mono-data">{i.number}</span><span className="font-mono-data">{da(i.totals.ttc)}</span>
              </Link>
            ))}
          </Panel>
        )}
      </div>
      <ClientDialog open={edit} onOpenChange={setEdit} initial={c} onSaved={reload} />
      <VehicleDialog open={veh} onOpenChange={setVeh} clientId={c.id} onSaved={reload} />
    </div>
  );
}
